import { Injectable, NgZone, OnDestroy, inject } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { finalize, share } from 'rxjs/operators';

/** Состояния вкладки браузера */
export enum BrowserTabState {
  /** Вкладка активна и обрабатывает события */
  ACTIVE = 'ACTIVE',
  /** Вкладка "заморожена" и не обрабатывает события */
  FROZEN = 'FROZEN',
}

/**
 * Сервис для мониторинга состояния вкладки браузера.
 * Определяет когда вкладка "замороживается" браузером и становится вновь активной
 * @example
 * private readonly browserTabLifecycleService = inject(BrowserTabLifecycleService);
 *
 * constructor() {
 *   this.browserTabLifecycleService.onTabLifecycleChange()
 *     .subscribe(state => {
 *       if (state === BrowserTabState.ACTIVE) {
 *         console.log('Вкладка активна');
 *       }
 *     });
 * }
 */
@Injectable({
  providedIn: 'root',
})
export class BrowserTabLifecycleService implements OnDestroy {
  private readonly ngZone = inject(NgZone);

  /** Интервал проверки состояния в миллисекундах */
  private readonly CHECK_INTERVAL = 1000;

  /** Порог времени для определения "заморозки" вкладки в миллисекундах */
  private readonly FREEZE_THRESHOLD = 3000;

  /** Минимальное время нахождения в замороженном состоянии в миллисекундах */
  private readonly MIN_FREEZE_DURATION = 5000;

  /** Subject для оповещения об изменении состояния вкладки */
  private tabState$ = new Subject<BrowserTabState>();

  /** Временная метка последней проверки */
  private lastTimestamp = performance.now();

  /** Флаг состояния заморозки вкладки */
  private isFrozen = false;

  /** Временная метка начала заморозки */
  private freezeStartTime: number | null = null;

  /** Идентификатор таймера для проверки состояния */
  private timerId: any;

  /** Количество активных подписчиков */
  private subscribersCount = 0;

  /** Общий поток состояний для всех подписчиков */
  private sharedTabState$: Observable<BrowserTabState>;

  /**
   * @param ngZone Сервис Angular для выполнения кода вне зоны Angular
   */
  constructor() {
    this.sharedTabState$ = this.tabState$.pipe(
      share(),
      finalize(() => {
        this.subscribersCount--;
        if (this.subscribersCount === 0) {
          this.stopMonitoring();
        }
      }),
    );
  }

  /**
   * Запускает процесс мониторинга состояния вкладки
   */
  private startMonitoring(): void {
    if (this.timerId) {
      return;
    }

    this.ngZone.runOutsideAngular(() => {
      this.timerId = setInterval(() => {
        const now = performance.now();
        const elapsed = now - this.lastTimestamp;
        this.checkFreezeState(elapsed);
        this.lastTimestamp = now;
      }, this.CHECK_INTERVAL);
    });
  }

  /**
   * Останавливает процесс мониторинга состояния вкладки
   */
  private stopMonitoring(): void {
    if (this.timerId) {
      clearInterval(this.timerId);
      this.timerId = null;
    }
  }

  /**
   * Проверяет состояние заморозки вкладки
   * @param elapsed Время в миллисекундах, прошедшее с последней проверки
   */
  private checkFreezeState(elapsed: number): void {
    if (elapsed > this.FREEZE_THRESHOLD) {
      this.handleFreeze();
    } else {
      this.handleUnfreeze();
    }
  }

  /**
   * Обрабатывает переход вкладки в состояние заморозки
   */
  private handleFreeze(): void {
    if (!this.isFrozen) {
      this.isFrozen = true;
      this.freezeStartTime = Date.now();
      this.tabState$.next(BrowserTabState.FROZEN);
    }
  }

  /**
   * Обрабатывает выход вкладки из состояния заморозки
   */
  private handleUnfreeze(): void {
    if (this.isFrozen && this.freezeStartTime) {
      const freezeDuration = Date.now() - this.freezeStartTime;

      if (freezeDuration >= this.MIN_FREEZE_DURATION) {
        this.isFrozen = false;
        this.freezeStartTime = null;
        this.tabState$.next(BrowserTabState.ACTIVE);
      } else {
        this.isFrozen = false;
        this.freezeStartTime = null;
      }
    }
  }

  /**
   * Возвращает Observable для подписки на изменения состояния вкладки
   */
  public onTabLifecycleChange(): Observable<BrowserTabState> {
    this.subscribersCount++;
    if (this.subscribersCount === 1) {
      this.startMonitoring();
    }
    return this.sharedTabState$;
  }

  /**
   * Освобождает ресурсы при уничтожении сервиса
   */
  ngOnDestroy(): void {
    this.stopMonitoring();
    this.tabState$.complete();
  }
}
